import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left: Category Dropdown + Home + Guida */}
          <div className="flex items-center gap-2" ref={dropdownRef}>
            {/* Desktop Category Dropdown */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-medium cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
              >
                <i className="ri-menu-line text-base"></i>
                <span>Categorie</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-2xl shadow-lg border border-background-200/60 py-2 max-h-[420px] overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer group whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 group-hover:bg-accent-500 transition-colors flex-shrink-0"></span>
                      <span className="font-medium text-foreground-800">{cat.nameIt}</span>
                      <span className="text-foreground-400 text-xs ml-auto">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Nav Links */}
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-label font-medium text-foreground-600 cursor-pointer whitespace-nowrap hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <i className="ri-home-3-line"></i>
              <span>Home</span>
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-label font-medium text-foreground-600 cursor-pointer whitespace-nowrap hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <i className="ri-shopping-bag-3-line"></i>
              <span>Guida all&apos;acquisto</span>
            </a>
          </div>

          {/* Center: Brand */}
          <div className="flex items-center gap-2">
            <span className="text-xl md:text-2xl font-heading font-bold text-foreground-950 tracking-tight">Itaobuy</span>
            <span className="hidden sm:inline text-lg md:text-xl font-heading font-light text-foreground-400 tracking-wide">Spreadsheet</span>
          </div>

          {/* Right: CTA Buttons + Mobile Hamburger */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex px-3 py-2 md:px-5 md:py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs md:text-sm font-label font-medium cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors items-center gap-1.5"
            >
              <span>itaobuy sheet</span>
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex px-3 py-2 md:px-5 md:py-2.5 rounded-full bg-accent-500 text-background-50 text-xs md:text-sm font-label font-medium cursor-pointer whitespace-nowrap hover:bg-accent-600 transition-colors items-center gap-1.5"
            >
              <span>buy it now</span>
              <i className="ri-shopping-bag-line text-sm"></i>
            </a>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer bg-foreground-950 text-background-50"
              aria-label="Menu"
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 top-16 bg-background-50 z-40 md:hidden overflow-y-auto" ref={mobileRef}>
          <div className="px-4 py-6 space-y-6">
            {/* Mobile CTA buttons */}
            <div className="flex gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-medium cursor-pointer whitespace-nowrap text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                itaobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-label font-medium cursor-pointer whitespace-nowrap text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                buy it now
              </a>
            </div>

            {/* Mobile Nav Links */}
            <div className="flex gap-2">
              <Link
                to="/"
                className="flex-1 px-4 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-label font-medium cursor-pointer whitespace-nowrap text-center flex items-center justify-center gap-1.5"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-3-line"></i> Home
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-label font-medium cursor-pointer whitespace-nowrap text-center flex items-center justify-center gap-1.5"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-shopping-bag-3-line"></i> Guida acquisto
              </a>
            </div>

            {/* Mobile Categories Section */}
            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-1 py-3 border-b border-background-200 cursor-pointer"
              >
                <span className="text-base font-heading font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-grid-line text-accent-500"></i> Categorie Prodotti
                </span>
                <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="mt-2 space-y-1 pb-4">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-foreground-500"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-label font-semibold text-foreground-900">{cat.nameIt}</p>
                        <p className="text-xs text-foreground-500 mt-0.5">{cat.descriptionIt}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Articles Quick Links */}
            <div>
              <p className="text-xs font-label font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">Guide SEO</p>
              <div className="space-y-1">
                <Link to="/cos-e-itaobuy-spreadsheet" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-book-open-line text-accent-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-label font-medium text-foreground-800">Cos&apos;è Itaobuy Spreadsheet</p>
                    <p className="text-xs text-foreground-500">12 min di lettura</p>
                  </div>
                </Link>
                <Link to="/come-funziona-itaobuy" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-guide-line text-secondary-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-label font-medium text-foreground-800">Come Funziona Itaobuy</p>
                    <p className="text-xs text-foreground-500">15 min di lettura</p>
                  </div>
                </Link>
                <Link to="/itaobuy-sicuro" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-accent-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-label font-medium text-foreground-800">Itaobuy è Sicuro?</p>
                    <p className="text-xs text-foreground-500">10 min di lettura</p>
                  </div>
                </Link>
                <Link to="/migliori-prodotti-itaobuy" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-line text-secondary-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-label font-medium text-foreground-800">Migliori Prodotti Itaobuy</p>
                    <p className="text-xs text-foreground-500">18 min di lettura</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}